import { Store } from "vuex";
import { StoreState } from "@/shims-vuex";
import { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { authorizationService } from "@/services";
import AuthorizationService from "@/services/authorization-service";
import { RoleId } from "@/entities";
//import {AuthorizationService} from './guards';

/**
 * Exposed information to the middlewares.
 */
export interface MiddlewareContext {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  store: Store<StoreState>;
}

/**
 * A function that inspect the routing and does something.
 *
 * This can be used as a point to reject routing if the user is not
 * authenticated or if it doesnt have permission to access this route.
 */
export interface Middleware {
  handle(context: MiddlewareContext): Promise<MiddlewareHandlerResult>;
}

export interface MiddlewareHandlerResult {
  action: "reject" | "continue";
  redirect?: RouteLocationRaw;
}

export class MiddlewareChain implements Middleware {
  public middlewares: Middleware[];
  public context: MiddlewareContext;

  constructor(context: MiddlewareContext, middlewares: Middleware[] = []) {
    this.middlewares = middlewares;
    this.context = context;
  }

  public async handle(): Promise<MiddlewareHandlerResult> {
    for (const middleware of this.middlewares) {
      const handlerRes = await middleware.handle(this.context);
      if (handlerRes.action == "reject") {
        return handlerRes;
      }
    }
    return { action: "continue" };
  }
}
export class EnsureAuthDataValid implements Middleware {
  public async handle(
    context: MiddlewareContext
  ): Promise<MiddlewareHandlerResult> {
    if (!context.store.state.authUser.hasBeenValidated) {
      await context.store.dispatch("authUser/getAuthUser");
    }
    return { action: "continue" };
  }
}

export class AuthenticationMiddleware implements Middleware {
  public async handle(
    context: MiddlewareContext
  ): Promise<MiddlewareHandlerResult> {
    if (!context.store.state.authUser.isAuthenticated) {
      return { action: "reject", redirect: "/login" };
    }
    return { action: "continue" };
  }
}

export class AuthorizationMiddleware implements Middleware {
  private authorizationService: AuthorizationService;

  constructor(authorizationService: AuthorizationService) {
    this.authorizationService = authorizationService;
  }

  public async handle(
    context: MiddlewareContext
  ): Promise<MiddlewareHandlerResult> {
    const permissions = (context.to.meta.permissions ?? new Map()) as Map<
      string,
      Record<string, "from-params" | (() => unknown)>
    >;
    const permissionWithContextMap = new Map();
    permissions.forEach((contextKeys, permission) => {
      const permissionContext = {} as Record<string, unknown>;
      Object.entries(contextKeys).forEach(([contextKey, howToResolve]) => {
        if (howToResolve == "from-params") {
          permissionContext[contextKey] = context.to.params[contextKey];
        } else if (typeof howToResolve === "function") {
          permissionContext[contextKey] = howToResolve();
        }
      });
      permissionWithContextMap.set(permission, permissionContext);
    });

    let passedAll = true;
    for (const [permission, context] of permissionWithContextMap.entries()) {
      if (!(await this.authorizationService.can(permission, context))) {
        passedAll = false;
        break;
      }
    }
    if (!passedAll) {
      if (context.store.state.authUser.role?.id === RoleId.Citizen) {
        return { action: "reject", redirect: "/citizen" };
      } else {
        return { action: "reject", redirect: "/admin" }; // All users except admin
      }
    }
    return { action: "continue" };
  }
}

export const authenticationMiddleware = new AuthenticationMiddleware();
export const authorizationMiddleware = new AuthorizationMiddleware(
  authorizationService
);
